/**
 * WeaveIn.ai Platform - Organizations (CBO) API Service
 * TypeScript service layer for multi-tenant organization management
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
// API Version for cache busting
export const API_VERSION = '2.0.1-proxy-routing';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Organization {
  id: number;
  name: string;
  description?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  logo_url?: string;
  /** List-view logo: the org's Branding logo when set, else logo_url,
      normalized to a URL that actually serves (read-only). */
  display_logo_url?: string;
  status: 'active' | 'inactive';
  settings?: Record<string, any>;
  created_at: string;
  updated_at?: string;
  // Sponsor fields
  is_sponsor?: boolean;
  sponsor_status?: 'INACTIVE' | 'ACTIVE' | 'PENDING' | 'SUSPENDED';
  sponsor_since?: string;
  sponsor_id?: number; // ID from sponsor_organizations table
  sponsor_type?: string; // e.g., 'STATE_AGENCY', 'HEALTH_PLAN'
  // Customer status fields
  customer_status?: 'CUSTOMER' | 'OPPORTUNITY' | 'DEMO' | 'PROSPECT' | 'TRIAL' | 'CHURNED' | 'INACTIVE';
  is_test_data?: boolean;
  // Billing provider fields
  billing_model?: 'trust_npi' | 'organization_npi';
  npi?: string;
  ein?: string;
  taxonomy_code?: string;
  is_billing_provider?: boolean;
  billing_name?: string;
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  billing_phone?: string;
  billing_contact_name?: string;
  billing_contact_email?: string;
  pay_to_address_line1?: string;
  pay_to_address_line2?: string;
  pay_to_city?: string;
  pay_to_state?: string;
  pay_to_zip?: string;
}

export interface OrganizationCreate {
  name: string;
  description?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  logo_url?: string;
  status?: 'active' | 'inactive';
  settings?: Record<string, any>;
  // Sponsor fields
  is_sponsor?: boolean;
  sponsor_status?: 'INACTIVE' | 'ACTIVE' | 'PENDING' | 'SUSPENDED';
  // Customer status fields
  customer_status?: 'CUSTOMER' | 'OPPORTUNITY' | 'DEMO' | 'PROSPECT' | 'TRIAL' | 'CHURNED' | 'INACTIVE';
  // Billing provider fields
  billing_model?: 'trust_npi' | 'organization_npi';
  npi?: string;
  ein?: string;
  taxonomy_code?: string;
  is_billing_provider?: boolean;
  billing_name?: string;
  billing_address_line1?: string;
  billing_address_line2?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  billing_phone?: string;
  billing_contact_name?: string;
  billing_contact_email?: string;
  pay_to_address_line1?: string;
  pay_to_address_line2?: string;
  pay_to_city?: string;
  pay_to_state?: string;
  pay_to_zip?: string;
}

export interface UserOrganization {
  id: number;
  user_id: number;
  cbo_id: number;
  role: 'super_admin' | 'org_admin' | 'ops_admin' | 'manager' | 'staff' | 'viewer';
  is_primary: boolean;
  created_at: string;
  organization?: Organization;
}

export interface OrganizationStats {
  total_members: number;
  total_referrals: number;
  total_programs: number;
  active_enrollments: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

class OrganizationsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    // Use baseURL from environment variable (trimmed to handle trailing whitespace/newlines)
    // Build timestamp: 2025-11-02 09:30 PST
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    // Add auth token interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('🏢 Organizations API: No auth token found in localStorage');
      }
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }

      console.log('🏢 Organizations API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasToken: !!token,
        cboId: cboId || 'none'
      });
      return config;
    });

    // Add response error interceptor for detailed logging
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response?.status;
        const url = error.config?.url;
        const method = error.config?.method?.toUpperCase();

        if (status === 401) {
          console.error('🏢 Organizations API 401 Unauthorized:', {
            url,
            method,
            message: error.response?.data?.detail || error.message,
            hasToken: !!localStorage.getItem('auth_token'),
            cboId: localStorage.getItem('active_cbo_id') || 'none'
          });
        } else if (status === 405) {
          console.error('🏢 Organizations API 405 Method Not Allowed:', {
            url,
            method,
            message: error.response?.data?.detail || error.message
          });
        } else {
          console.error('🏢 Organizations API Error:', {
            status,
            url,
            method,
            message: error.response?.data?.detail || error.message
          });
        }
        return Promise.reject(error);
      }
    );
  }

  // ========================================================================
  // ORGANIZATION ENDPOINTS
  // ========================================================================

  /**
   * List all organizations (super admin) or user's organizations (org admin)
   */
  async listOrganizations(): Promise<Organization[]> {
    const response = await this.client.get<Organization[]>('/api/cbos');
    return response.data;
  }

  /**
   * Get organization details
   */
  async getOrganization(orgId: number): Promise<Organization> {
    const response = await this.client.get<Organization>(`/api/cbos/${orgId}`);
    return response.data;
  }

  /**
   * Create new organization (super admin only)
   */
  async createOrganization(data: OrganizationCreate): Promise<Organization> {
    const response = await this.client.post<Organization>('/api/cbos', data);
    return response.data;
  }

  /**
   * Update organization details
   */
  async updateOrganization(orgId: number, data: Partial<OrganizationCreate>): Promise<Organization> {
    const response = await this.client.put<Organization>(`/api/cbos/${orgId}`, data);

    // Dispatch event to notify OrganizationSwitcher to refresh its list
    // This is especially important when status changes (activate/deactivate)
    window.dispatchEvent(new Event('org-updated'));

    return response.data;
  }

  /**
   * Delete organization (soft delete - set status to inactive)
   */
  async deleteOrganization(orgId: number): Promise<void> {
    await this.client.delete(`/api/cbos/${orgId}`);
  }

  /**
   * Get organization statistics
   */
  async getOrganizationStats(orgId: number): Promise<OrganizationStats> {
    const response = await this.client.get<OrganizationStats>(`/api/cbos/${orgId}/stats`);
    return response.data;
  }

  // ========================================================================
  // USER-ORGANIZATION ENDPOINTS
  // ========================================================================

  /**
   * Get current user's organizations
   */
  async getMyOrganizations(): Promise<UserOrganization[]> {
    const response = await this.client.get<UserOrganization[]>('/api/cbos/users/me/organizations');
    return response.data;
  }

  /**
   * Set active organization for current session
   */
  async setActiveOrganization(orgId: number): Promise<void> {
    await this.client.post('/api/cbos/users/me/active-cbo', { cbo_id: orgId });
    // Update localStorage
    localStorage.setItem('active_cbo_id', orgId.toString());
    // Dispatch event to notify contexts (like BrandingContext) of organization change
    window.dispatchEvent(new Event('organizationChanged'));
  }

  /**
   * Get users in an organization
   */
  async getOrganizationUsers(orgId: number): Promise<UserOrganization[]> {
    const response = await this.client.get<UserOrganization[]>(`/api/cbos/${orgId}/users`);
    return response.data;
  }

  /**
   * Add user to organization
   */
  async addUserToOrganization(
    orgId: number,
    userId: number,
    role: 'super_admin' | 'org_admin' | 'ops_admin' | 'manager' | 'staff' | 'viewer'
  ): Promise<UserOrganization> {
    const response = await this.client.post<UserOrganization>(`/api/cbos/${orgId}/users`, {
      user_id: userId,
      role,
    });
    return response.data;
  }

  /**
   * Update user role in organization
   */
  async updateUserRole(
    orgId: number,
    userId: number,
    role: 'super_admin' | 'org_admin' | 'ops_admin' | 'manager' | 'staff' | 'viewer'
  ): Promise<UserOrganization> {
    const response = await this.client.put<UserOrganization>(`/api/cbos/${orgId}/users/${userId}`, {
      role,
    });
    return response.data;
  }

  /**
   * Remove user from organization
   */
  async removeUserFromOrganization(orgId: number, userId: number): Promise<void> {
    await this.client.delete(`/api/cbos/${orgId}/users/${userId}`);
  }
}

// Export singleton instance - uses env var or empty string for proxy routing
export const organizationsAPI = new OrganizationsAPI();

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get active organization ID from localStorage
 */
export const getActiveCboId = (): number | null => {
  const cboId = localStorage.getItem('active_cbo_id');
  return cboId ? parseInt(cboId, 10) : null;
};

/**
 * Check if user is super admin
 */
export const isSuperAdmin = (userOrgs: UserOrganization[]): boolean => {
  return userOrgs.some(org => org.role === 'super_admin');
};

/**
 * Check if user is org admin for given organization
 */
export const isOrgAdmin = (userOrgs: UserOrganization[], orgId: number): boolean => {
  return userOrgs.some(org => org.cbo_id === orgId && (org.role === 'org_admin' || org.role === 'super_admin'));
};

/**
 * Get user's role in an organization
 */
export const getUserRoleInOrg = (userOrgs: UserOrganization[], orgId: number): string | null => {
  const org = userOrgs.find(o => o.cbo_id === orgId);
  return org ? org.role : null;
};
